import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Loader2, SearchIcon } from "lucide-react";
import CloseButton from "../assets/CloseButton";
import SmallLoadingSpinner from "../assets/SmallLoadingSpinner";
import { useIngredientCache } from "../context/IngredientCacheContext";
import { Ingredient } from "../types/Types";

interface CachedIngredientSelectByNameProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  label?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  enableRef?: boolean;
  disabled?: boolean;
}

const CachedIngredientSelectByName = forwardRef<HTMLInputElement, CachedIngredientSelectByNameProps>(
  ({ value, onChange, placeholder, required = false, onKeyDown, enableRef = false, label = true, disabled = false }, ref) => {
  const { getIngredient, searchIngredients, isLoading } = useIngredientCache();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [filteredIngredients, setFilteredIngredients] = useState<Ingredient[]>([]);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const ignoreBlur = useRef(false);
    const isTypingRef = useRef(false);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Fetch ingredient by value if not in cache
    useEffect(() => {
      if (isTypingRef.current) return;
      if (!value) {
        setSearchTerm("");
        return;
      }
      const ingredient = getIngredient(value);
      if (ingredient) {
        const itemName = typeof ingredient.items === 'object' ? ingredient.items.name : '';
        setSearchTerm(itemName || "");
      } else {
        const apiUrl = process.env.REACT_APP_API_URL ?? '';
        const token = localStorage.getItem('token');
        fetch(`${apiUrl}/api/ingredients/${value}`, { headers: { 'x-auth-token': token ?? '' } })
          .then(res => res.json())
          .then(data => {
            if (!isTypingRef.current) {
              if (data && data._id && data.items) {
                const itemName = typeof data.items === 'object' ? data.items.name : '';
                setSearchTerm(itemName || "");
              } else {
                setSearchTerm("");
              }
            }
          })
          .catch(() => { if (!isTypingRef.current) setSearchTerm(""); });
      }
    }, [value, getIngredient]);

    // Reset typing flag when dropdown closes
    useEffect(() => {
      if (!isOpen) {
        isTypingRef.current = false;
      }
    }, [isOpen]);

    // Debounced update of filtered ingredients when search term changes or dropdown opens
    useEffect(() => {
      if (isOpen) {
        const handler = setTimeout(async () => {
          try {
            const result = await searchIngredients(searchTerm, 1);
            // Filter client-side to match by item name
            let filteredByName = result.ingredients.filter(ingredient => {
              const itemName = typeof ingredient.items === 'object' ? ingredient.items.name : '';
              return !searchTerm || itemName?.toLowerCase().includes(searchTerm.toLowerCase());
            });

            setFilteredIngredients(filteredByName);
            setHasMore(result.hasMore);
            setCurrentPage(1);
          } catch (error) {
            console.error('Error searching ingredients by name:', error);
            setFilteredIngredients([]);
            setHasMore(false);
          }
        }, 300); // 300ms debounce
        return () => clearTimeout(handler);
      }
    }, [searchTerm, isOpen, searchIngredients]);

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          !inputRef.current?.contains(e.target as Node) &&
          !dropdownRef.current?.contains(e.target as Node)
        ) {
          setIsOpen(false);
          setHighlighted(-1);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
      if (highlighted >= 0 && dropdownRef.current) {
        const item = dropdownRef.current.children[highlighted] as HTMLElement;
        item?.scrollIntoView({ block: "nearest" });
      }
    }, [highlighted]);

    const updateDropdownPosition = () => {
      if (inputRef.current && label === false) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    const loadMoreIngredients = async () => {
      if (!hasMore || isLoading) return;

      try {
        const result = await searchIngredients(searchTerm, currentPage + 1);
        const filteredByName = result.ingredients.filter(ingredient => {
          const itemName = typeof ingredient.items === 'object' ? ingredient.items.name : '';
          return !searchTerm || itemName?.toLowerCase().includes(searchTerm.toLowerCase());
        });
        if (filteredByName.length > 0) {
          setFilteredIngredients(prev => [...prev, ...filteredByName]);
          setCurrentPage(prev => prev + 1);
          // Note: No scroll restoration needed - browser handles this correctly
          // when appending items to a scrollable list
        }
        setHasMore(result.hasMore);
      } catch (error) {
        console.error('Error loading more ingredients:', error);
        setHasMore(false);
      }
    };


    const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
      const element = e.currentTarget;
      // Use 100px threshold to trigger load before reaching absolute bottom
      // This provides smoother UX and reduces scroll jump issues during fast scrolling
      const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 100;

      // Only load more if we're near bottom, have more items, and not currently loading
      if (isNearBottom && hasMore && !isLoading) {
        loadMoreIngredients();
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      isTypingRef.current = true;
      setSearchTerm(val);
      if (val === "") {
        onChange("");
        setHighlighted(-1);
        setIsOpen(true); // keep dropdown open ready for new search
        if (label === false) updateDropdownPosition();
        return;
      }
      if (!isOpen) setIsOpen(true);
      setHighlighted(-1);
      if (label === false) updateDropdownPosition();
    };

    const handleSelect = (ingredient: Ingredient) => {
      onChange(ingredient._id);
      const itemName = typeof ingredient.items === 'object' ? ingredient.items.name : '';
      setSearchTerm(itemName || "");
      setIsOpen(false);
      isTypingRef.current = false;
    };

    const handleClear = () => {
      isTypingRef.current = true; // treat as start of new typing session
      onChange("");
      setSearchTerm("");
      setHighlighted(-1);
      setIsOpen(true);
      if (label === false) updateDropdownPosition();
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle dropdown-specific keys first
      if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      // Handle navigation and selection within the dropdown
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlighted((i) => Math.min(i + 1, filteredIngredients.length - 1));
          return; // Don't call parent handler for navigation
        case "ArrowUp":
          e.preventDefault();
          setHighlighted((i) => Math.max(i - 1, -1));
          return; // Don't call parent handler for navigation
        case "Enter":
          // If dropdown is open and an item is highlighted, select it
          if (isOpen && highlighted >= 0 && filteredIngredients[highlighted]) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(filteredIngredients[highlighted]);
            return; // Don't call parent handler after selection
          } else if (isOpen && filteredIngredients.length === 0) {
            e.preventDefault();
            setSearchTerm("");
            return; // Don't call parent handler for clearing
          }
          // If dropdown is closed or no item highlighted, let parent handle Enter
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setHighlighted(-1);
          return; // Don't call parent handler for escape
      }

      // Call parent onKeyDown handler for other keys (like Enter when dropdown is closed)
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    // Render compact mode for tables
    if (label === false) {
      return (
        <div className="w-full h-6">
          <div className="relative h-6">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                setIsOpen(true);
                updateDropdownPosition();
              }}
              onBlur={() => {
                if (!ignoreBlur.current) setIsOpen(false);
              }}
              onKeyDown={handleKeyDown}
              className="w-full h-6 px-1 bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
              autoComplete="off"
              placeholder=""
            />

            {/* Show loading spinner only when actively searching and cache is empty */}
            {isLoading && !searchTerm && !value && filteredIngredients.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                <SmallLoadingSpinner size="xs" />
              </div>
            )}
          </div>

          {/* Render dropdown using portal to avoid table clipping */}
          {isOpen && dropdownPosition && createPortal(
            <ul
              ref={dropdownRef}
              onMouseDown={() => (ignoreBlur.current = true)}
              onMouseUp={() => (ignoreBlur.current = false)}
              onScroll={handleScroll}
              style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 99999
              }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-scroll max-h-[150px]"
              role="listbox"
            >
              {isLoading ? (
                <li className="px-4 py-3 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-4 w-4 text-blue-500" />
                </li>
              ) : filteredIngredients.length === 0 ? (
                <li className="px-4 py-3 text-gray-500 bg-gray-50">
                  <div className="text-sm">{t("forms.noResults")}</div>
                </li>
              ) : (
                filteredIngredients.map((ingredient, index) => {
                  const itemCode = typeof ingredient.items === 'object' ? ingredient.items.ident : '';
                  const itemName = typeof ingredient.items === 'object' ? ingredient.items.name : '';
                  return (
                    <li
                      key={ingredient._id}
                      className={`px-4 py-2.5 cursor-pointer transition-colors ${
                        highlighted === index ? "bg-blue-50" : "hover:bg-gray-50"
                      } ${value === ingredient._id ? "bg-blue-100 border-l-4 border-blue-500" : ""}`}
                      onClick={() => handleSelect(ingredient)}
                      onMouseEnter={() => setHighlighted(index)}
                      role="option"
                      aria-selected={value === ingredient._id}
                    >
                      <div className="font-semibold text-gray-900 text-sm">
                        {itemName}
                      </div>
                      <div className="text-xs text-gray-500">{itemCode}</div>
                    </li>
                  );
                })
              )}
              {/* Loading indicator for infinite scroll */}
              {hasMore && !isLoading && (
                <li className="px-4 py-2 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-3 w-3 text-blue-500" />
                </li>
              )}
              {isLoading && currentPage > 1 && (
                <li className="px-4 py-2 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-3 w-3 text-blue-500" />
                </li>
              )}
            </ul>,
            document.body
          )}
        </div>
      );
    }

    // Full mode
    return (
      <div className="w-full">
        <div className="relative">
          <div
            className={`flex items-center border rounded-lg transition-all duration-200 ${
              disabled
                ? "bg-gray-50 cursor-not-allowed border-gray-200"
                : isOpen
                ? "bg-white border-blue-500 ring-2 ring-blue-100 cursor-text"
                : "bg-white border-gray-300 hover:border-gray-400 cursor-text"
            }`}
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon size={18} />
            </div>

            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => !disabled && setIsOpen(true)}
              onBlur={() => {
                if (!ignoreBlur.current) setIsOpen(false);
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t("tableHeaders.name")}
              className={`w-full py-2.5 pl-10 pr-10 bg-transparent focus:outline-none ${
                disabled ? 'cursor-not-allowed text-gray-500' : ''
              }`}
              autoComplete="off"
              disabled={disabled}
            />

            {(value || searchTerm) && !disabled && (
              <div className="flex items-center pr-2">
                <CloseButton onClick={handleClear} />
              </div>
            )}
          </div>

          {isOpen && !disabled && (
            <ul
              ref={dropdownRef}
              onMouseDown={() => (ignoreBlur.current = true)}
              onMouseUp={() => (ignoreBlur.current = false)}
              onScroll={handleScroll}
              className="absolute z-[99999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-scroll max-h-[200px]"
              role="listbox"
            >
              {filteredIngredients.length === 0 && !isLoading ? (
                <li className="px-4 py-3 text-gray-500 bg-gray-50">{t("forms.noResults")}</li>
              ) : filteredIngredients.length === 0 && isLoading ? (
                <li className="px-4 py-3 text-gray-500 flex items-center justify-center">
                  <Loader2 className="animate-spin h-5 w-5 text-blue-500" />
                </li>
              ) : (
                <>
                  {filteredIngredients.map((ingredient, index) => {
                    const itemCode = typeof ingredient.items === 'object' ? ingredient.items.ident : '';
                    const itemName = typeof ingredient.items === 'object' ? ingredient.items.name : '';
                    return (
                      <li
                        key={ingredient._id}
                        className={`px-4 py-2.5 cursor-pointer transition-colors ${
                          highlighted === index ? "bg-blue-50" : "hover:bg-gray-50"
                        } ${value === ingredient._id ? "bg-blue-100 border-l-4 border-blue-500" : ""}`}
                        onClick={() => handleSelect(ingredient)}
                        onMouseEnter={() => setHighlighted(index)}
                        role="option"
                        aria-selected={value === ingredient._id}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{itemName}</div>
                            <div className="text-sm text-gray-600">{itemCode}</div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {/* Loading indicator for infinite scroll - only at bottom */}
                  {isLoading && currentPage > 1 && (
                    <li className="px-4 py-2 text-gray-500 flex items-center justify-center bg-blue-50">
                      <Loader2 className="animate-spin h-4 w-4 text-blue-500" />
                    </li>
                  )}
                </>
              )}
            </ul>
          )}
        </div>
      </div>
    );
  }
);

export default CachedIngredientSelectByName;

